/**
 * ‍ ADAPTIVE INTERVAL TRAINING SYSTEM
 *
 * Intelligent HIIT progression based on per-interval performance data.
 * Unlike weight training (which focuses on load), interval training progression
 * is driven by:
 *
 * 1. FATIGUE RESISTANCE - How well does the athlete maintain distance across intervals?
 *    - Good: <10% drop from first to last interval → ready for progression
 *    - Moderate: 10-20% drop → maintain current protocol
 *    - Poor: >20% drop → reduce volume or increase rest
 *
 * 2. DISTANCE OUTPUT - Are they covering more ground per interval over time?
 *    - Track average distance per interval across sessions
 *    - Progressive improvement = fitness adaptation
 *
 * 3. CONSISTENCY - How consistent are distances across intervals?
 *    - Low variance = good pacing, ready for harder work
 *    - High variance = struggling to pace, needs more practice
 *
 * 4. RPE TREND - Overall perceived effort across sessions
 *    - Similar to weight training RPE-based progression
 *
 * PROGRESSION PATHWAYS:
 * - Add intervals: 5 → 6 → 7 → 8 sets
 * - Reduce rest: 30/60s → 30/45s → 30/30s
 * - Increase work: 30/60s → 45/60s → 60/60s
 * - Progress format: 30/60s → 60/120s when ready
 *
 * @module adaptiveInterval
 */

import { getSessionHistory, type ExerciseLog, type IntervalSetLog } from './sessionStorage';
import { getCurrentUserId } from './anonymousAuth';
import { db } from './db';
import type { PeriodizationPhase } from './periodization';

// ============================================================================
// TYPES
// ============================================================================

export type IntervalFormat = '30/60' | '30/45' | '30/30' | '60/120' | '60/90' | '60/60' | 'custom';
export type IntervalProgressionPath =
  | 'add_intervals'      // Add more sets
  | 'reduce_rest'        // Decrease rest period
  | 'increase_work'      // Increase work period
  | 'upgrade_format'     // Move to harder format (e.g., 30/60 → 60/120)
  | 'maintain'           // Keep current protocol
  | 'deload';            // Reduce volume

export interface IntervalPerformanceMetrics {
  // Per-session metrics
  totalDistance: number;           // Total meters covered
  avgDistancePerInterval: number;  // Average meters per interval
  intervalCount: number;           // Number of intervals completed

  // Fatigue analysis
  firstIntervalDistance: number;   // Distance in first interval
  lastIntervalDistance: number;    // Distance in last interval
  fatigueDropPercent: number;      // % drop from first to last
  peakIntervalDistance: number;    // Best single interval

  // Consistency analysis
  distanceVariance: number;        // Standard deviation of distances
  consistencyScore: number;        // 0-100 (100 = perfectly consistent)

  // RPE
  rpe: number | null;

  // Session info
  sessionDate: string;
  workDuration: number;            // Work interval in seconds
  restDuration: number;            // Rest interval in seconds
}

export interface IntervalProgressionDecision {
  exerciseName: string;
  currentFormat: IntervalFormat;
  currentIntervals: number;

  // Recommended changes
  suggestedFormat?: IntervalFormat;
  suggestedIntervals?: number;

  // Decision details
  progressionPath: IntervalProgressionPath;
  reason: string;
  confidence: 'high' | 'medium' | 'low';

  // Performance summary
  avgFatigueDrop: number;          // Average fatigue drop % over recent sessions
  avgDistancePerInterval: number;  // Average distance across sessions
  avgRPE: number;                  // Average RPE across sessions
  consistencyTrend: 'improving' | 'stable' | 'declining';

  // Data quality
  sessionsAnalyzed: number;
  readyForProgression: boolean;
}

export interface IntervalAdaptiveResult {
  decisions: IntervalProgressionDecision[];
  summary: string;
  totalExercises: number;
  readyToProgress: number;
  needsReduction: number;
  maintaining: number;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Parse interval format from exercise name
 * e.g., "30/60s Sprint" → { work: 30, rest: 60 }
 */
function parseIntervalFormat(exerciseName: string): { work: number; rest: number; format: IntervalFormat } | null {
  // Match patterns like "30/60", "60/120", etc.
  const match = exerciseName.match(/(\d+)\/(\d+)/);
  if (!match) return null;

  const work = parseInt(match[1]);
  const rest = parseInt(match[2]);

  // Determine format category
  let format: IntervalFormat = 'custom';
  if (work === 30 && rest === 60) format = '30/60';
  else if (work === 30 && rest === 45) format = '30/45';
  else if (work === 30 && rest === 30) format = '30/30';
  else if (work === 60 && rest === 120) format = '60/120';
  else if (work === 60 && rest === 90) format = '60/90';
  else if (work === 60 && rest === 60) format = '60/60';

  return { work, rest, format };
}

/**
 * Calculate standard deviation for consistency analysis
 */
function calculateStdDev(values: number[]): number {
  if (values.length === 0) return 0;
  const mean = values.reduce((sum, v) => sum + v, 0) / values.length;
  const squaredDiffs = values.map(v => Math.pow(v - mean, 2));
  return Math.sqrt(squaredDiffs.reduce((sum, v) => sum + v, 0) / values.length);
}

/**
 * Convert consistency variance to a 0-100 score
 * Lower variance = higher score
 */
function calculateConsistencyScore(distances: number[]): number {
  if (distances.length < 2) return 100;

  const mean = distances.reduce((sum, d) => sum + d, 0) / distances.length;
  const stdDev = calculateStdDev(distances);

  // Coefficient of variation (CV) as percentage
  const cv = (stdDev / mean) * 100;

  // Convert to score (0 CV = 100 score, 30% CV = 0 score)
  return Math.max(0, Math.min(100, 100 - (cv * 3.33)));
}

/**
 * Get interval exercise history
 */
async function getIntervalExerciseHistory(exerciseName: string): Promise<ExerciseLog[]> {
  const sessions = await getSessionHistory();
  const history: ExerciseLog[] = [];

  sessions.forEach(session => {
    if (session.sessionType !== 'range' && session.exercises) {
      session.exercises.forEach(ex => {
        // Match exercise name and ensure it has interval data
        if (ex.exerciseName.toLowerCase().includes(exerciseName.toLowerCase()) ||
            exerciseName.toLowerCase().includes(ex.exerciseName.toLowerCase())) {
          if (ex.intervalSets && ex.intervalSets.length > 0) {
            history.push({
              ...ex,
              // Store session date for sorting
              exerciseName: ex.exerciseName,
            });
          }
        }
      });
    }
  });

  return history;
}

/**
 * Calculate performance metrics for a single interval session
 */
function calculateSessionMetrics(
  exerciseLog: ExerciseLog,
  format: { work: number; rest: number; format: IntervalFormat }
): IntervalPerformanceMetrics | null {
  const sets = exerciseLog.intervalSets;
  if (!sets || sets.length === 0) return null;

  const completedSets = sets.filter(s => s.completed && s.distance > 0);
  if (completedSets.length < 2) return null; // Need at least 2 for fatigue analysis

  const distances = completedSets.map(s => s.distance);
  const totalDistance = distances.reduce((sum, d) => sum + d, 0);
  const avgDistance = totalDistance / distances.length;

  // Fatigue analysis
  const firstDistance = distances[0];
  const lastDistance = distances[distances.length - 1];
  const fatigueDropPercent = ((firstDistance - lastDistance) / firstDistance) * 100;
  const peakDistance = Math.max(...distances);

  // Consistency
  const variance = calculateStdDev(distances);
  const consistencyScore = calculateConsistencyScore(distances);

  return {
    totalDistance,
    avgDistancePerInterval: avgDistance,
    intervalCount: completedSets.length,
    firstIntervalDistance: firstDistance,
    lastIntervalDistance: lastDistance,
    fatigueDropPercent,
    peakIntervalDistance: peakDistance,
    distanceVariance: variance,
    consistencyScore,
    rpe: exerciseLog.actualRPE ?? null,
    sessionDate: new Date().toISOString(), // Will be overwritten by caller
    workDuration: format.work,
    restDuration: format.rest,
  };
}

// ============================================================================
// CORE ADAPTIVE LOGIC
// ============================================================================

/**
 * Analyze interval progression for a specific exercise
 */
export async function analyzeIntervalProgression(
  exerciseName: string,
  lookbackSessions: number = 5,
  recoveryScore?: number,
  periodizationPhase?: PeriodizationPhase
): Promise<IntervalProgressionDecision | null> {
  // Parse format from exercise name
  const format = parseIntervalFormat(exerciseName);
  if (!format) {
    console.warn(` Could not parse interval format from "${exerciseName}"`);
    return null;
  }

  // Get history
  const history = await getIntervalExerciseHistory(exerciseName);

  if (history.length < 2) {
    // Not enough data - return maintain decision
    return {
      exerciseName,
      currentFormat: format.format,
      currentIntervals: 5, // Default
      progressionPath: 'maintain',
      reason: `Need at least 2 sessions for interval analysis. Current: ${history.length}`,
      confidence: 'low',
      avgFatigueDrop: 0,
      avgDistancePerInterval: 0,
      avgRPE: 7,
      consistencyTrend: 'stable',
      sessionsAnalyzed: history.length,
      readyForProgression: false,
    };
  }

  // Calculate metrics for each session
  const sessionMetrics: IntervalPerformanceMetrics[] = [];
  for (const session of history.slice(-lookbackSessions)) {
    const metrics = calculateSessionMetrics(session, format);
    if (metrics) {
      sessionMetrics.push(metrics);
    }
  }

  if (sessionMetrics.length < 2) {
    return {
      exerciseName,
      currentFormat: format.format,
      currentIntervals: history[history.length - 1]?.intervalSets?.length || 5,
      progressionPath: 'maintain',
      reason: 'Insufficient interval data quality for analysis',
      confidence: 'low',
      avgFatigueDrop: 0,
      avgDistancePerInterval: 0,
      avgRPE: 7,
      consistencyTrend: 'stable',
      sessionsAnalyzed: sessionMetrics.length,
      readyForProgression: false,
    };
  }

  // Calculate aggregate metrics
  const avgFatigueDrop = sessionMetrics.reduce((sum, m) => sum + m.fatigueDropPercent, 0) / sessionMetrics.length;
  const avgDistancePerInterval = sessionMetrics.reduce((sum, m) => sum + m.avgDistancePerInterval, 0) / sessionMetrics.length;
  const avgConsistency = sessionMetrics.reduce((sum, m) => sum + m.consistencyScore, 0) / sessionMetrics.length;
  const avgRPE = sessionMetrics.reduce((sum, m) => sum + (m.rpe || 7), 0) / sessionMetrics.length;
  const currentIntervals = sessionMetrics[sessionMetrics.length - 1].intervalCount;

  // Calculate consistency trend
  let consistencyTrend: 'improving' | 'stable' | 'declining' = 'stable';
  if (sessionMetrics.length >= 3) {
    const recentConsistency = sessionMetrics.slice(-2).reduce((sum, m) => sum + m.consistencyScore, 0) / 2;
    const olderConsistency = sessionMetrics.slice(0, -2).reduce((sum, m) => sum + m.consistencyScore, 0) / Math.max(1, sessionMetrics.length - 2);
    const diff = recentConsistency - olderConsistency;
    if (diff > 5) consistencyTrend = 'improving';
    else if (diff < -5) consistencyTrend = 'declining';
  }

  // ===== DECISION LOGIC =====
  let progressionPath: IntervalProgressionPath = 'maintain';
  let reason = '';
  let confidence: 'high' | 'medium' | 'low' = 'medium';
  let suggestedFormat: IntervalFormat | undefined;
  let suggestedIntervals: number | undefined;
  let readyForProgression = false;

  // ===== PERIODIZATION OVERRIDE =====
  if (periodizationPhase?.type === 'deload') {
    progressionPath = 'deload';
    suggestedIntervals = Math.max(3, currentIntervals - 2);
    reason = `Deload week: Reduce intervals from ${currentIntervals} to ${suggestedIntervals}`;
    confidence = 'high';
    return {
      exerciseName,
      currentFormat: format.format,
      currentIntervals,
      suggestedFormat,
      suggestedIntervals,
      progressionPath,
      reason,
      confidence,
      avgFatigueDrop,
      avgDistancePerInterval,
      avgRPE,
      consistencyTrend,
      sessionsAnalyzed: sessionMetrics.length,
      readyForProgression: false,
    };
  }

  // ===== RECOVERY-BASED ADJUSTMENT =====
  if (recoveryScore !== undefined && recoveryScore < 70) {
    progressionPath = recoveryScore < 60 ? 'deload' : 'maintain';
    suggestedIntervals = recoveryScore < 60 ? Math.max(3, currentIntervals - 1) : currentIntervals;
    reason = `Low recovery (${recoveryScore}/100). ${recoveryScore < 60 ? 'Reduce volume' : 'Maintain current protocol'}.`;
    confidence = 'high';
    return {
      exerciseName,
      currentFormat: format.format,
      currentIntervals,
      suggestedFormat,
      suggestedIntervals,
      progressionPath,
      reason,
      confidence,
      avgFatigueDrop,
      avgDistancePerInterval,
      avgRPE,
      consistencyTrend,
      sessionsAnalyzed: sessionMetrics.length,
      readyForProgression: false,
    };
  }

  // ===== FATIGUE-BASED ANALYSIS =====

  // Rule 1: High fatigue drop (>20%) - needs more rest or fewer intervals
  if (avgFatigueDrop > 20) {
    progressionPath = 'deload';
    suggestedIntervals = Math.max(3, currentIntervals - 1);
    reason = `High fatigue drop (${avgFatigueDrop.toFixed(0)}%) - reduce intervals or consider longer rest periods`;
    confidence = 'high';
  }
  // Rule 2: Moderate fatigue drop (10-20%) + high RPE - maintain
  else if (avgFatigueDrop > 10 && avgRPE >= 8) {
    progressionPath = 'maintain';
    reason = `Moderate fatigue (${avgFatigueDrop.toFixed(0)}%) with high RPE (${avgRPE.toFixed(1)}) - building capacity at current level`;
    confidence = 'medium';
  }
  // Rule 3: Moderate fatigue drop (10-20%) + lower RPE - maintain but close to progression
  else if (avgFatigueDrop > 10 && avgRPE < 8) {
    progressionPath = 'maintain';
    reason = `Good adaptation - fatigue (${avgFatigueDrop.toFixed(0)}%) managing well at RPE ${avgRPE.toFixed(1)}. Close to progression threshold.`;
    confidence = 'medium';
  }
  // Rule 4: Low fatigue drop (<10%) + good consistency + moderate RPE = ready for progression!
  else if (avgFatigueDrop <= 10 && avgConsistency >= 70) {
    readyForProgression = true;

    // Determine progression path
    if (avgRPE < 7) {
      // Very comfortable - consider harder format
      if (format.format === '30/60' && sessionMetrics.length >= 4) {
        progressionPath = 'upgrade_format';
        suggestedFormat = '60/120';
        reason = `Excellent fatigue resistance (${avgFatigueDrop.toFixed(0)}%) and low RPE (${avgRPE.toFixed(1)}) - ready for 60/120s intervals`;
        confidence = 'high';
      } else if (format.format === '60/120' && currentIntervals < 7) {
        progressionPath = 'add_intervals';
        suggestedIntervals = currentIntervals + 1;
        reason = `Strong performance at 60/120s - add interval ${currentIntervals + 1}`;
        confidence = 'high';
      } else if (currentIntervals < 8) {
        progressionPath = 'add_intervals';
        suggestedIntervals = currentIntervals + 1;
        reason = `Low fatigue drop (${avgFatigueDrop.toFixed(0)}%) and RPE (${avgRPE.toFixed(1)}) - add another interval`;
        confidence = 'high';
      } else {
        progressionPath = 'reduce_rest';
        reason = `Maxed out on intervals. Consider reducing rest period for more challenge.`;
        confidence = 'medium';
      }
    } else if (avgRPE < 8) {
      // Moderate effort - add intervals
      if (currentIntervals < 8) {
        progressionPath = 'add_intervals';
        suggestedIntervals = currentIntervals + 1;
        reason = `Good fatigue resistance (${avgFatigueDrop.toFixed(0)}%) at manageable RPE (${avgRPE.toFixed(1)}) - add interval`;
        confidence = 'medium';
      } else {
        progressionPath = 'maintain';
        reason = `Strong at ${currentIntervals} intervals. Maintain for continued adaptation.`;
        confidence = 'medium';
      }
    } else {
      // RPE 8+ but good fatigue resistance - maintain
      progressionPath = 'maintain';
      reason = `Good fatigue resistance but RPE is challenging (${avgRPE.toFixed(1)}). Continue building at current level.`;
      confidence = 'medium';
    }
  }
  // Default maintain
  else {
    progressionPath = 'maintain';
    reason = `Building capacity - fatigue: ${avgFatigueDrop.toFixed(0)}%, consistency: ${avgConsistency.toFixed(0)}%`;
    confidence = 'low';
  }

  return {
    exerciseName,
    currentFormat: format.format,
    currentIntervals,
    suggestedFormat,
    suggestedIntervals,
    progressionPath,
    reason,
    confidence,
    avgFatigueDrop,
    avgDistancePerInterval,
    avgRPE,
    consistencyTrend,
    sessionsAnalyzed: sessionMetrics.length,
    readyForProgression,
  };
}

/**
 * Analyze all interval exercises from a session
 */
export async function analyzeSessionIntervals(
  exercises: ExerciseLog[],
  recoveryScore?: number,
  periodizationPhase?: PeriodizationPhase
): Promise<IntervalAdaptiveResult> {
  const decisions: IntervalProgressionDecision[] = [];

  // Filter to interval exercises
  const intervalExercises = exercises.filter(ex =>
    ex.exerciseType === 'interval' && ex.intervalSets && ex.intervalSets.length > 0
  );

  console.log(`‍ Adaptive Interval: Analyzing ${intervalExercises.length} interval exercises...`);

  for (const exercise of intervalExercises) {
    const decision = await analyzeIntervalProgression(
      exercise.exerciseName,
      5,
      recoveryScore,
      periodizationPhase
    );
    if (decision) {
      decisions.push(decision);
    }
  }

  // Save decisions
  await saveIntervalProgressionDecisions(decisions);

  // Count categories
  const readyToProgress = decisions.filter(d => d.readyForProgression).length;
  const needsReduction = decisions.filter(d => d.progressionPath === 'deload').length;
  const maintaining = decisions.filter(d => d.progressionPath === 'maintain').length;

  // Generate summary
  let summary = '';
  if (readyToProgress > 0) {
    summary += `${readyToProgress} interval exercise${readyToProgress > 1 ? 's' : ''} ready for progression. `;
  }
  if (needsReduction > 0) {
    summary += `${needsReduction} need${needsReduction > 1 ? '' : 's'} volume reduction. `;
  }
  if (summary === '') {
    summary = 'Interval training on track at current levels.';
  }

  console.log(` Adaptive Interval: ${readyToProgress} progress, ${needsReduction} deload, ${maintaining} maintain`);

  return {
    decisions,
    summary: summary.trim(),
    totalExercises: decisions.length,
    readyToProgress,
    needsReduction,
    maintaining,
  };
}

// ============================================================================
// STORAGE: Save and Load Interval Progression Decisions
// ============================================================================

async function saveIntervalProgressionDecisions(decisions: IntervalProgressionDecision[]): Promise<void> {
  try {
    const userId = getCurrentUserId();
    if (!userId) {
      console.warn('No user ID available for saving interval progression decisions');
      return;
    }

    // Save each decision to Dexie
    for (const d of decisions) {
      const id = `${userId}_interval_${d.exerciseName.toLowerCase().replace(/\s+/g, '_')}`;
      await db.intervalProgressDecisions.put({
        id,
        userId,
        exerciseName: d.exerciseName,
        currentFormat: d.currentFormat,
        currentIntervals: d.currentIntervals,
        suggestedFormat: d.suggestedFormat,
        suggestedIntervals: d.suggestedIntervals,
        progressionPath: d.progressionPath,
        reason: d.reason,
        confidence: d.confidence,
        avgFatigueDrop: d.avgFatigueDrop,
        avgDistancePerInterval: d.avgDistancePerInterval,
        avgRPE: d.avgRPE,
        consistencyTrend: d.consistencyTrend,
        sessionsAnalyzed: d.sessionsAnalyzed,
        readyForProgression: d.readyForProgression,
        updatedAt: new Date(),
      });
    }

    console.log(` Saved ${decisions.length} interval progression decisions to Dexie`);
  } catch (error) {
    console.error('Failed to save interval progression decisions:', error);
  }
}

export async function loadIntervalProgressionDecisions(): Promise<IntervalProgressionDecision[]> {
  try {
    const userId = getCurrentUserId();
    if (!userId) return [];

    const decisions = await db.intervalProgressDecisions
      .where('userId')
      .equals(userId)
      .toArray();

    // Keep decisions for 30 days (was 7 — too aggressive, users take rest weeks)
    const now = new Date();
    const validDecisions = decisions.filter(d => {
      const daysSinceUpdate = (now.getTime() - d.updatedAt.getTime()) / (1000 * 60 * 60 * 24);
      return daysSinceUpdate <= 30;
    });

    // Clean up very stale decisions (>30 days)
    const staleIds = decisions
      .filter(d => {
        const daysSinceUpdate = (now.getTime() - d.updatedAt.getTime()) / (1000 * 60 * 60 * 24);
        return daysSinceUpdate > 30;
      })
      .map(d => d.id);

    if (staleIds.length > 0) {
      await db.intervalProgressDecisions.bulkDelete(staleIds);
      console.log(` Cleaned up ${staleIds.length} stale interval progression decisions (>30 days)`);
    }

    return validDecisions;
  } catch (error) {
    console.error('Failed to load interval progression decisions:', error);
    return [];
  }
}

/**
 * Get progression decision for a specific interval exercise
 */
export async function getIntervalProgression(exerciseName: string): Promise<IntervalProgressionDecision | null> {
  const decisions = await loadIntervalProgressionDecisions();
  return decisions.find(d =>
    d.exerciseName.toLowerCase().includes(exerciseName.toLowerCase()) ||
    exerciseName.toLowerCase().includes(d.exerciseName.toLowerCase())
  ) || null;
}

/**
 * Apply interval progression to workout generation
 * Returns adjusted interval count and format recommendations
 */
export async function applyIntervalProgression(
  exerciseName: string,
  baseIntervals: number,
  baseFormat: IntervalFormat
): Promise<{
  intervals: number;
  format: IntervalFormat;
  applied: boolean;
  reason?: string;
}> {
  const decision = await getIntervalProgression(exerciseName);

  if (!decision || decision.progressionPath === 'maintain') {
    return { intervals: baseIntervals, format: baseFormat, applied: false };
  }

  let adjustedIntervals = baseIntervals;
  let adjustedFormat = baseFormat;

  switch (decision.progressionPath) {
    case 'add_intervals':
      adjustedIntervals = decision.suggestedIntervals || baseIntervals + 1;
      break;
    case 'reduce_rest':
      // Map to tighter rest format
      if (baseFormat === '30/60') adjustedFormat = '30/45';
      else if (baseFormat === '60/120') adjustedFormat = '60/90';
      break;
    case 'upgrade_format':
      adjustedFormat = decision.suggestedFormat || baseFormat;
      break;
    case 'deload':
      adjustedIntervals = decision.suggestedIntervals || Math.max(3, baseIntervals - 1);
      break;
  }

  console.log(`‍ Adaptive Interval: ${exerciseName} | ${baseIntervals}x ${baseFormat} → ${adjustedIntervals}x ${adjustedFormat} (${decision.reason})`);

  return {
    intervals: adjustedIntervals,
    format: adjustedFormat,
    applied: true,
    reason: decision.reason,
  };
}

/**
 * Clear all interval progression decisions (e.g., after deload week)
 */
export async function clearIntervalProgressionDecisions(): Promise<void> {
  try {
    const userId = getCurrentUserId();
    if (!userId) return;

    await db.intervalProgressDecisions.where('userId').equals(userId).delete();
    console.log('� Cleared all interval progression decisions');
  } catch (error) {
    console.error('Failed to clear interval progression decisions:', error);
  }
}

// ============================================================================
// DASHBOARD HELPERS
// ============================================================================

/**
 * Get interval training summary for dashboard/coach brief
 */
export async function getIntervalTrainingSummary(): Promise<{
  hasData: boolean;
  readyForProgression: string[];
  recommendations: string[];
}> {
  const decisions = await loadIntervalProgressionDecisions();

  if (decisions.length === 0) {
    return {
      hasData: false,
      readyForProgression: [],
      recommendations: ['Complete 2+ interval sessions to enable adaptive tracking'],
    };
  }

  const readyForProgression = decisions
    .filter(d => d.readyForProgression)
    .map(d => d.exerciseName);

  const recommendations: string[] = [];

  for (const d of decisions) {
    if (d.readyForProgression) {
      switch (d.progressionPath) {
        case 'add_intervals':
          recommendations.push(` ${d.exerciseName}: Add interval ${d.suggestedIntervals}`);
          break;
        case 'upgrade_format':
          recommendations.push(` ${d.exerciseName}: Ready for ${d.suggestedFormat} format`);
          break;
        case 'reduce_rest':
          recommendations.push(` ${d.exerciseName}: Tighten rest periods`);
          break;
      }
    } else if (d.progressionPath === 'deload') {
      recommendations.push(`⬇ ${d.exerciseName}: Reduce to ${d.suggestedIntervals} intervals`);
    }
  }

  return {
    hasData: true,
    readyForProgression,
    recommendations: recommendations.length > 0 ? recommendations : ['Interval training on track'],
  };
}

// ============================================================================
// EXPORTS
// ============================================================================

export {
  parseIntervalFormat,
  calculateSessionMetrics,
  calculateConsistencyScore,
};
